import { sendUserEmail } from "@/apis/v1/user";
import PermissionGuard from "@/components/Guard/PermissionGuard";
import DeleteIcon from "@/components/Icon/DeleteIcon";
import EditIcon from "@/components/Icon/EditIcon";
import LinkIcon from "@/components/Icon/LinkIcon";
import SendEmailIcon from "@/components/Icon/SendEmailIcon";
import LoginAsIcon from "@/components/Icon/LoginAsIcon";
import ActionCell from "@/components/Table/ActionCell";
import Cell from "@/components/Table/Cell";
import { useDialogDispatch } from "@/context/DialogContext";
import { useSnackbarDispatch } from "@/context/SnackbarContext";
import { useUserContext } from "@/context/UserContext";
import useLoadingDialog from "@/hooks/useLoadingDialog";
import usePermission from "@/hooks/usePermission";
import { messages } from "@/messages";
import routes from "@/routes";
import { Box, Button, IconButton, OutlinedInput, Tooltip } from "@mui/material";
import { QueryObserverResult, useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { permissionMouleByUserType } from "../data";
import {
  useCompleteProfileUrl,
  useDeleteUser,
  useLoginAsUser,
} from "../user.hook";

const UserTableRow = ({ row, usersRefetch }: AdvertiserTableRowsProps) => {
  const { isAdmin, isAgencyAdmin, isAsAdvertiser } = usePermission();

  const { setSnackbarState } = useSnackbarDispatch();
  const { loginAsSomeUser, previousUserToken, user, doesPreviousUserExists } =
    useUserContext();
  const {
    refetch: getCompleteProfileUrl,
    isLoading: getCompleteProfileUrlIsLoading,
    fetchStatus: getCompleteProfileUrlFetchStatus,
  } = useCompleteProfileUrl({
    userId: row.id,
  });
  const { data: loginAsUserData, refetch } = useLoginAsUser({
    userId: row.id,
    token: previousUserToken,
  });
  const {
    mutateAsync: sendUserEmailMutate,
    isLoading: sendUserEmailIsLoading,
  } = useMutation(sendUserEmail);
  const { setDialogState, closeDialog } = useDialogDispatch();
  const navigate = useNavigate();
  function handleLoginAsClick() {
    refetch();
  }

  const { mutateAsync: deleteUser, isLoading: isDeletingUser } =
    useDeleteUser();

  useLoadingDialog(isDeletingUser);

  async function handleConfirmDeleteUser() {
    const res = await deleteUser({
      userId: row.id,
    });
    await usersRefetch();
    setSnackbarState({
      open: true,
      message: res.message,
    });
    closeDialog();
  }

  function handleEditClick() {
    navigate(routes.USERS.EDIT(row.id), { state: row });
  }

  function handleDeleteClick() {
    setDialogState({
      open: true,
      variant: "alert",
      title: messages.alertPrompt(),
      buttonLoading: isDeletingUser,
      buttonFunction: handleConfirmDeleteUser,
      children: <>{messages.delete("user")}</>,
    });
  }

  // Named once rather than inlined into both the button and its icon, so the
  // control and the glyph can never disagree about being in flight.
  const getLinkLoading = Boolean(
    getCompleteProfileUrlIsLoading &&
      getCompleteProfileUrlFetchStatus === "fetching"
  );

  async function handleGetCompleteProfileUrlClick() {
    const res = await getCompleteProfileUrl();
    setDialogState({
      open: true,
      title: "User Link",
      children: <UserLink value={res.data.CompleteProfileUser} />,
    });
  }

  async function handleSendUserEmailClick() {
    const res = await sendUserEmailMutate({
      userId: row.id,
      email: row.email,
    });
    setSnackbarState({
      open: true,
      message: res.message,
    });
  }

  if (loginAsUserData) {
    loginAsSomeUser({
      data: loginAsUserData,
    });
  }
  const permissionModule = permissionMouleByUserType({
    userRole: user.Role.name,
    userCompanyId: user.companyId,
    listUsercompanyId: row.companyId,
  });
  const userActionDisabled = Boolean(
    !isAdmin && (user.id === row.id || row.isSuperUser)
  );

  return (
    <>
      <Cell columnId="name">{row.Username}</Cell>
      <Cell columnId="email">{row.email}</Cell>
      <Cell columnId="company">{row.company.name}</Cell>
      <Cell columnId="createdAt">
        {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ""}
      </Cell>
      <ActionCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PermissionGuard module={permissionModule} permissionType="update">
            <Tooltip title="Send Email" describeChild>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  onClick={handleSendUserEmailClick}
                  disabled={sendUserEmailIsLoading}
                  aria-label="Send Email"
                >
                  <SendEmailIcon disabled={sendUserEmailIsLoading} />
                </IconButton>
              </Box>
            </Tooltip>
          </PermissionGuard>
          <PermissionGuard module={permissionModule} permissionType="update">
            {/* The label used to carry the state — "Get Link" flipping to
                "Updated" once a user has onboarded — so with the label gone the
                tooltip carries it instead, in the same words. The disabled
                state says the same thing visually, and the span keeps the
                tooltip alive while the button is dead. */}
            <Tooltip title={row.onBoarding ? "Updated" : "Get Link"} describeChild>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  onClick={handleGetCompleteProfileUrlClick}
                  disabled={row.onBoarding || getLinkLoading}
                  aria-label={row.onBoarding ? "Updated" : "Get Link"}
                >
                  <LinkIcon disabled={row.onBoarding || getLinkLoading} />
                </IconButton>
              </Box>
            </Tooltip>
          </PermissionGuard>
          {(doesPreviousUserExists || isAdmin || isAgencyAdmin) &&
            !isAsAdvertiser && (
              // These three disable together on `userActionDisabled` — acting on
              // your own row, or on a super user — and a disabled MUI button
              // fires no pointer events, so without the span the tooltip goes
              // silent and all you get is three greyed, unlabelled icons.
              <Tooltip title="Login As" describeChild>
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <IconButton
                    onClick={handleLoginAsClick}
                    disabled={userActionDisabled}
                    aria-label="Login As"
                  >
                    <LoginAsIcon disabled={userActionDisabled} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          <PermissionGuard module={permissionModule} permissionType="update">
            <Tooltip title="Edit" describeChild>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  onClick={handleEditClick}
                  disabled={userActionDisabled}
                  aria-label="Edit"
                >
                  <EditIcon disabled={userActionDisabled} />
                </IconButton>
              </Box>
            </Tooltip>
          </PermissionGuard>

          <PermissionGuard module={permissionModule} permissionType="delete">
            <Tooltip title="Delete" describeChild>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  onClick={handleDeleteClick}
                  disabled={userActionDisabled}
                  aria-label="Delete"
                >
                  <DeleteIcon disabled={userActionDisabled} />
                </IconButton>
              </Box>
            </Tooltip>
          </PermissionGuard>
        </Box>
      </ActionCell>
    </>
  );
};

export default UserTableRow;

interface AdvertiserTableRowsProps {
  row: any;
  usersRefetch: () => Promise<QueryObserverResult<any, unknown>>;
}

function UserLink({ value }: { value: string }) {
  const [isCopied, setIsCopied] = React.useState(false);

  async function copyTextToClipboard(text: string) {
    if ("clipboard" in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand("copy", true, text);
    }
  }
  const handleCopyClick = () => {
    copyTextToClipboard(value)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <OutlinedInput readOnly value={value} />
      </Box>
      <Button variant="outlined" onClick={handleCopyClick}>
        {isCopied ? "Copied!" : "Copy"}
      </Button>
    </Box>
  );
}
